import {
    useEffect,
    useState,
    useCallback
} from 'react';

import deepEqual from 'deep-equal';
import {
    useLocation,
    useNavigate
} from 'react-router-dom';
import { t } from 'i18next';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { IoNotificationsSharp, IoNotificationsOutline } from "react-icons/io5";

import styles from './JobsList.module.css';
import JobsCard from '../JobsCard/JobsCard';
import Pagination from '../../Pagination/Pagination';
import BlockLoader from '../../../../../components/UI/Loaders/BlockLoader';
import JobsFullView from '../JobsFullView/JobsFullView';
import VerificationBanner from '../VerificationBanner/VerificationBanner';
import { getJobsList } from '../../../../../services/jobService';
import {  SortIconList } from '../../../../../assets/images/icons';
import { createJobAlert, getJobAlerts, deleteJobAlert } from '../../../../../services/jobAlertService';

function JobsList({ per_page, max_pages, hide_url_params = false, filters, onPageChange, initialSortBy, onFetchJobs, handleSortbyChange, children, ...props }) {
    const navigate = useNavigate();
    const location = useLocation();

    const recentJobs = location.state?.job; // Access the passed job
    const query = new URLSearchParams(location.search);
    const initialPage = parseInt(query.get('page')) || 1;
    const [loading, setLoading] = useState(true);
    const [paginationLoading, setPaginationLoading] = useState(false);
    const [page, setPage] = useState(initialPage);
    const [filtersList, setFiltersList] = useState(filters);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [totalRelevantJobsCount, setTotalRelevantJobsCount] = useState(0);
    const [jobs, setJobs] = useState([]);
    const [jobAlerts, setJobAlerts] = useState([]);
    const [relevancyIndex, setEndRelevancyIndex] = useState(-1)
    const [fullViewList, setFullViewList] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [sortBy, setSortBy] = useState(initialSortBy || 'relevance')
   const user = useSelector(state => state.user.user);

    const updateURL = useCallback((params) => {
        delete params.employer_id;
        delete params.include_ids;
        delete params.exclude_ids;
        delete params.status;

        const searchParams = new URLSearchParams(params);
        navigate(`?${searchParams.toString()}`);
    }, [navigate]);

    useEffect(() => {
        if (filters) {

            if (!deepEqual(filters, filtersList)) {
                setFiltersList(filters);
                setPage(1);

                if (!hide_url_params) {
                    updateURL({ page: 1, ...filters });
                }
            }
        }
    }, [filters, hide_url_params, filtersList, page, updateURL]);

    useEffect(() => {
        if (recentJobs?.length > 0) {
            setTotalCount(recentJobs.length);
            setTotalPages(Math.ceil(recentJobs.length / 10));
            setLoading(false);
            
        } else {
            const MAX_RETRIES = 1000; // Maximum retries
            let retryCount = 0;
            const fetchJobs = (page, per_page, max_pages, filters, onFetchJobs) => {
                setLoading(true);
                setPaginationLoading(true);
                getJobsList(page, per_page, max_pages, filters && Object.keys(filters).length > 0 ? filters : {}, user?.employer_status || user?.jobseeker_status,user?.jobseeker_status?user?.id:'' )
                    .then((response) => { 
                        // if (response?.jobs?.length > 0) {
                        const specifiedCountryJobs = response?.jobs?.filter((e) => e?.specifiedforcountry == 'true' && e?.country !== user?.country);
                        const specifiedCityJobs = response?.jobs?.filter((e) => e?.specifiedforcity == 'true' && e?.city !== user?.city);
                        const filteredJobs = response?.jobs?.filter((e) => { return !(specifiedCountryJobs?.includes(e) || specifiedCityJobs?.includes(e)); });
                        if (user?.account_type == 'employer') {
                            let getIndexOfRelevance = response?.jobs.findIndex((item) => item.isRelevent == false)
                            setEndRelevancyIndex(getIndexOfRelevance)
                            setJobs(response?.jobs);
                        }
                        else {
                            let getIndexOfRelevance = filteredJobs.findIndex((item) => item.isRelevent == false)
                            setEndRelevancyIndex(getIndexOfRelevance)
                            setJobs(filteredJobs);
                        }
                        // }
                        setTotalPages(response?.totalPages);
                        setTotalCount(response?.total);
                        setTotalRelevantJobsCount(response?.totalRelevantJobsCount || 0);
                        setLoading(false);
                        setPaginationLoading(false);

                        if (onFetchJobs) {
                            onFetchJobs({
                                total: response?.total,
                                active: response?.active,
                                paused: response?.paused,
                                closed: response?.closed
                            });
                        }

                        retryCount = 0;
                    })
                    .catch((error) => {
                        if (error.error === 'out of shared memory' && retryCount < MAX_RETRIES) {
                            retryCount++;
                            console.log(`Retrying... Attempt ${retryCount}`);
                            fetchJobs(page, per_page, max_pages, filters, onFetchJobs);
                        } else {
                            console.error('Failed to fetch jobs after retries:', error);
                            setLoading(false);
                            setPaginationLoading(false);
                        }
                    });
            };
            if (filters) {
                fetchJobs(page, per_page, max_pages, filters, onFetchJobs);
            }

        }
    }, [page, recentJobs, filters, filtersList, per_page]);

    const handlePageChange = (value) => {
        setPage(value);
        setPaginationLoading(true);
        const target = document.querySelector('.pagination_scroll_target');
        if (target) {
            window.scrollTo(0, target.offsetTop);
        }

        if (!hide_url_params) {
            updateURL({ page: value, ...filtersList });
        }
    };
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchJobAlerts = async () => {
            try {
                const jobseeker_id = user.id;
                const alerts = await getJobAlerts(jobseeker_id);
                setJobAlerts(alerts);
            } catch (error) {
            }
        };

        fetchJobAlerts();
    }, []);

    const handleJobAlert = async () => {
        if (!user?.id) {
            toast.error(t('jobs.login_required_for_job_alerts'));
            return;
        }

        try {
            // Get current search term from filters
            const search_term = filters?.search || '';
            
            // If we have job alerts and no search term, we're trying to remove an alert
            if (jobAlerts.length > 0 && (!search_term || search_term.trim() === '')) {
                // Get the first alert to remove
                const alertToRemove = jobAlerts[0];
                await deleteJobAlert(alertToRemove.id, user.id);
                toast.success(t('jobs.job_alert_removed'));
                // Refresh alerts list
                const updatedAlerts = await getJobAlerts(user.id);
                setJobAlerts(updatedAlerts);
                return;
            }

            // Check if alert already exists for the current search term
            const existingAlert = jobAlerts.find(alert => alert.search_term === search_term);

            if (existingAlert) {
                // Delete existing alert
                await deleteJobAlert(existingAlert.id, user.id);
                toast.success(t('jobs.job_alert_removed'));
                // Refresh alerts list
                const updatedAlerts = await getJobAlerts(user.id);
                setJobAlerts(updatedAlerts);
            } else {
                // Only require search term for creating new alerts
                if (!search_term || search_term.trim() === '') {
                    toast.error(t('jobs.search_term_required_for_job_alerts'));
                    return;
                }
                // Create new alert
                await createJobAlert(user.id, search_term);
                toast.success(t('jobs.job_alert_created'));
                // Refresh alerts list
                const updatedAlerts = await getJobAlerts(user.id);
                setJobAlerts(updatedAlerts);
            }
        } catch (error) {
            toast.error(t('jobs.job_alert_operation_failed') + ': ' + error.message);
        }
    };

    return (
        <div {...props} className={styles.list}>
            <div className={styles.talentsHeader}>
                <div className={styles.titleContainer}>
                    <p className={styles.headerTitleBold}>{totalRelevantJobsCount>0 ? totalRelevantJobsCount : totalCount}</p>
                    <p className={styles.headerTitle}>{t('jobs.jobs_matching_criteria')}</p>
                </div>
                <div className={styles.titleContainer}>
                    <img
                        src={SortIconList}
                        alt={t('talentsHeader.sortBy')}
                        className={styles.sortIcon}
                    />
                    <p
                        className={` ${styles.headerTitle} ${styles.sortButton}`}>{t('talentsHeader.sortBy')}</p>
                    <p
                        className={`${styles.headerTitleBold} ${styles.sortButton}`}>
                        <span
                            className={`${(sortBy == 'relevance') ? `${styles.isBold} ${styles.headerTitleBold}` : styles.headerTitleBold}`}
                            onClick={() => {
                                setSortBy('relevance')
                                handleSortbyChange(`relevance`)
                            }}
                        >{t('jobs.relevance')}</span>
                        {' - '}
                        <span
                            className={`${(sortBy == 'newest') ? `${styles.isBold} ${styles.headerTitleBold}` : styles.headerTitleBold}`}
                            onClick={() => {
                                setSortBy('newest')
                                handleSortbyChange(`newest`)
                            }}
                            style={{ textTransform: "capitalize" }}
                        >{t('jobs.most_recent')}</span>

                    </p>
                </div>
            </div>
            <div className={styles.talentsHeader}>
                <p className={styles.headerTitle}>{t('jobs.createJobAlert')}{filters?.search && ' (' + filters?.search + ')'}</p>
                {jobAlerts.length > 0 ? (
                    <IoNotificationsSharp
                        size={24}
                        color="#1E90FF"
                        onClick={handleJobAlert}
                        style={{ cursor: 'pointer' }}
                    />
                ) : (
                    <IoNotificationsOutline
                        size={24}
                        color="#1E90FF"
                        onClick={handleJobAlert}
                        style={{ cursor: 'pointer' }}
                    />
                )}
            </div>

            {(!recentJobs&&!loading && ((!jobs || jobs.length === 0) || relevancyIndex === 0)) && (
                <div className={styles.noJobsMessage}>
                    {t('jobs.no_listings_match_criteria')}
                </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexDirection: isMobile ? "column-reverse" : "row" }} >
                <div className={fullViewList ? isMobile ? `${styles.cardWrapperForMobile} scrollJobCard` : `${styles.cardWrapper} scrollJobCard` : `${styles.cardWrapperFullView} scrollJobCard`}>
                    {recentJobs ?
                        recentJobs.map((job, index) => {
                            return (
                                <>
                                    {index === 1 && user?.verification_status !== 'approved' && (<VerificationBanner />)}
                                    <JobsCard
                                        filters={filters}
                                        key={job.id}
                                        job={job}
                                        disabled={paginationLoading}
                                        fullViewList={fullViewList}
                                        setFullViewList={setFullViewList}
                                    />
                                </>
                            );
                        })
                        :
                        jobs &&
                        jobs.map((job, index) => {
                            return (
                                <>
                                    {index === 1 && user?.verification_status !== 'approved' && (
                                        <VerificationBanner />
                                    )}
                                    {relevancyIndex == index &&
                                        <div className={styles.otherJobsHeading}>
                                            {t(`jobs.Similar_jobs`)}
                                        </div>
                                    }
                                    <JobsCard
                                        key={job.id}
                                        filters={filters}
                                        job={job}
                                        disabled={paginationLoading}
                                        fullViewList={fullViewList}
                                        setFullViewList={setFullViewList}
                                    />
                                </>
                            );
                        }
                        )}
                </div>
                {fullViewList && (
                    <JobsFullView
                        fullViewList={fullViewList}
                        setFullViewList={setFullViewList}
                    />
                )}
            </div>

            {loading && (Array.from({ length: per_page }, (_, index) => {
                return <BlockLoader key={index} height={320} marginBottom={20} />;
            }))}

            {totalPages > 0 && <Pagination totalPages={totalPages} currentPage={page} onPageChange={handlePageChange} />}
        </div>
    );
}

export default JobsList;